import React, { useRef, useEffect, useState } from 'react';
import { OverlayView } from '@react-google-maps/api';
import styled from 'styled-components';
import { getInitials, getColorFromId } from './helper';

// Styled Components
const MarkerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const MarkerCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid #e6b471;
  overflow: hidden;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  color: white;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  }

  &.selected {
    z-index: 100;
    transform: translate(-50%, -50%) scale(1.15);
    box-shadow:
      0 0 0 2px white,
      0 0 12px rgba(0, 113, 227, 0.4);
  }
`;

const MarkerPulse = styled.div`
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(230, 180, 113, 0.3);
  transform: translate(-50%, -50%);
  animation: ${(props) => (props.isActive ? 'pulse 2s infinite' : 'none')};

  @keyframes pulse {
    0% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0;
    }
  }
`;

const FriendMarker = ({
  friend,
  isSelected = false,
  onClick = () => {},
  onMarkerMount = () => {},
  showPulse = false,
}) => {
  const markerElementRef = useRef(null);

  // Register marker element on mount
  useEffect(() => {
    if (markerElementRef.current) {
      onMarkerMount(friend._id, markerElementRef.current);
    }
  }, [friend._id, onMarkerMount]);

  // Update selected state styling
  useEffect(() => {
    if (markerElementRef.current) {
      if (isSelected) {
        markerElementRef.current.classList.add('selected');
      } else {
        markerElementRef.current.classList.remove('selected');
      }
    }
  }, [isSelected]);

  if (!friend?.location?.latitude || !friend?.location?.longitude) {
    console.warn('Invalid friend location data:', friend);
    return null;
  }

  const hasProfileImg = friend?.userInfo?.profileImg;
  const initials = getInitials(friend?.userInfo?.firstName, friend?.userInfo?.lastName);
  const bgColor = getColorFromId(friend._id);

  const position = {
    lat: parseFloat(friend.location.latitude),
    lng: parseFloat(friend.location.longitude),
  };

  const handleMarkerClick = () => {
    onClick(friend, true);
  };

  return (
    <OverlayView
      key={`friend-marker-${friend._id}`}
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <MarkerContainer
        ref={markerElementRef}
        onClick={handleMarkerClick}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleMarkerClick();
          }
        }}
        aria-label={`${friend.userInfo.firstName} ${friend.userInfo.lastName} location marker`}
      >
        {showPulse && <MarkerPulse isActive={true} />}
        <MarkerCircle bgColor={bgColor} className={isSelected ? 'selected' : ''}>
          {hasProfileImg ? (
            <img
              src={friend.userInfo.profileImg}
              alt={`${friend.userInfo.firstName} ${friend.userInfo.lastName}`}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            initials
          )}
        </MarkerCircle>
      </MarkerContainer>
    </OverlayView>
  );
};

export default FriendMarker;
